<template>
  <button
    type="button"
    class="bottom-nav-item app-focus-ring"
    :class="{ 'is-active': isActive, 'is-loading': isLoading }"
    :aria-current="isActive ? 'page' : undefined"
    :aria-label="item.label"
    @click="handleClick"
    @pointerdown="onPointerDown"
    @pointerup="onPointerEnd"
    @pointerleave="onPointerEnd"
    @pointercancel="onPointerEnd">
    <q-spinner
      v-if="isLoading"
      :size="22"
      class="bottom-nav-item__icon" />
    <q-icon
      v-else-if="item.icon"
      :name="item.icon"
      class="bottom-nav-item__icon" />
    <span class="bottom-nav-item__label">{{ item.label }}</span>
  </button>
</template>

<script setup lang="ts">
import { downloadReport } from 'src/CrudManager/Core/Utils/downloadReport'
import type { BottomNavSourceItem } from 'src/composables/useBottomNavSource'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  item: BottomNavSourceItem
}>()

const emit = defineEmits<{
  'long-press': []
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const isLoading = ref(false)
const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const longPressFired = ref(false)
const LONG_PRESS_MS = 500

const isActive = computed(() => {
  if (props.item.kind !== 'route' || !props.item.to) return false
  return route.path === props.item.to
})

const clearLongPress = () => {
  if (longPressTimer.value) {
    clearTimeout(longPressTimer.value)
    longPressTimer.value = null
  }
}

const onPointerDown = () => {
  longPressFired.value = false
  clearLongPress()
  longPressTimer.value = setTimeout(() => {
    longPressFired.value = true
    longPressTimer.value = null
    emit('long-press')
  }, LONG_PRESS_MS)
}

const onPointerEnd = () => {
  clearLongPress()
}

const handleClick = async () => {
  if (longPressFired.value) {
    longPressFired.value = false
    return
  }
  if (props.item.kind === 'download') {
    if (!props.item.downloadUrl || isLoading.value) return
    isLoading.value = true
    try {
      await downloadReport({
        url: props.item.downloadUrl,
        fallbackFilename: props.item.filename,
        successMessage: t('crudmanager.customMenu.download_success'),
        errorMessage: t('crudmanager.customMenu.download_error'),
        progressMessage: t('crudmanager.customMenu.download_progress'),
      })
    } finally {
      isLoading.value = false
    }
    return
  }
  if (props.item.to) {
    void router.push(props.item.to)
  }
}

onBeforeUnmount(clearLongPress)
</script>

<style lang="scss" scoped>
.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  flex: 1 1 0;
  min-width: 0;
  padding: 6px 4px;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.78);
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition:
    color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.06);
  }

  &.is-active {
    color: #fff;
    background-color: color-mix(in srgb, var(--q-primary) 32%, transparent);

    .bottom-nav-item__icon {
      transform: translateY(-1px);
    }
  }

  &.is-loading {
    pointer-events: none;
    color: var(--q-primary);
  }
}

.body--light .bottom-nav-item {
  color: rgba(0, 0, 0, 0.7);

  &:hover {
    color: #000;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &.is-active {
    color: var(--q-primary);
    background-color: color-mix(in srgb, var(--q-primary) 14%, transparent);
  }
}

.bottom-nav-item__icon {
  font-size: 1.45rem;
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.bottom-nav-item__label {
  font-size: 0.66rem;
  font-weight: 550;
  line-height: 1.1;
  letter-spacing: 0.01em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: capitalize;
}
</style>
